import { useEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import { AdminSidebar } from '../../components/Sidebar';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import api from '../../utils/api';

const PLAN_INFO = {
  free:     { label: 'Testphase',  color: '#666',    features: '30 Tage · alle Premium-Funktionen · nach Ablauf: Upgrade erforderlich' },
  standard: { label: 'Standard',   color: '#1A56DB', features: '50 Teilnehmer · 7 Quizze/Monat · 20 eigene Fragen · CSV-Import · PDF-Zertifikate · Statistiken · Einladungslinks' },
  premium:  { label: 'Premium',    color: '#8B6A1C', features: 'Unbegrenzte Teilnehmer & Quizze · Moscheen-Duelle · Fotos · Hintergrundbild · Eigene URL & Branding · Wissenskarte · Empfehlungscode' },
};

export default function AdminProfile() {
  const { mosque: authMosque, plan: authPlan, hasFeature } = useAuth();
  const toast = useToast();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('info');

  const logoInputRef = useRef(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoMsg, setLogoMsg] = useState('');

  // Change password state
  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  const [pwMsg, setPwMsg] = useState({ type: '', text: '' });
  const [pwLoading, setPwLoading] = useState(false);

  // Branding state
  const [brandColor, setBrandColor] = useState('');
  const [brandMsg, setBrandMsg] = useState({ type: '', text: '' });
  const [brandLoading, setBrandLoading] = useState(false);

  // Custom URL state
  const [urlSlug, setUrlSlug] = useState('');
  const [urlMsg, setUrlMsg] = useState({ type: '', text: '' });
  const [urlLoading, setUrlLoading] = useState(false);

  // Referral state
  const [referrals, setReferrals] = useState(null);

  // Plan prices from SuperAdmin settings
  const [planPrices, setPlanPrices] = useState({ standard_price_chf: null, premium_price_chf: null });

  // Info edit state
  const [infoEditing, setInfoEditing] = useState(false);
  const [infoForm, setInfoForm] = useState({});
  const [infoLoading, setInfoLoading] = useState(false);
  const [infoMsg, setInfoMsg] = useState({ type: '', text: '' });

  // Sub-admins state
  const [subAdmins, setSubAdmins] = useState([]);
  const [subAdminsLoading, setSubAdminsLoading] = useState(false);
  const [newSub, setNewSub] = useState({ name: '', email: '', password: '', role: 'admin' });
  const [subMsg, setSubMsg] = useState({ type: '', text: '' });
  const [subCreating, setSubCreating] = useState(false);

  useEffect(() => {
    api.get('/admin/profile')
      .then(r => {
        setProfile(r.data.mosque);
        if (r.data.mosque.custom_color) setBrandColor(r.data.mosque.custom_color);
        if (r.data.mosque.slug) setUrlSlug(r.data.mosque.slug);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (tab === 'plan') {
      api.get('/admin/plan-prices').then(r => setPlanPrices(r.data.prices)).catch(() => {});
      if (hasFeature('referral')) {
        api.get('/admin/referrals').then(r => setReferrals(r.data)).catch(() => {});
      }
    }
    if (tab === 'team') {
      setSubAdminsLoading(true);
      api.get('/admin/sub-admins').then(r => setSubAdmins(r.data.subAdmins)).catch(() => {}).finally(() => setSubAdminsLoading(false));
    }
  }, [tab, hasFeature]);

  const uploadLogo = async (file) => {
    if (!file) return;
    setLogoUploading(true); setLogoMsg('');
    try {
      const fd = new FormData();
      fd.append('logo', file);
      const r = await api.post('/admin/profile/logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setProfile(p => ({ ...p, logo_path: r.data.logoUrl }));
      setLogoMsg('Logo erfolgreich gespeichert');
    } catch (err) {
      setLogoMsg(err.response?.data?.error || 'Fehler beim Hochladen');
    } finally { setLogoUploading(false); }
  };

  const saveBranding = async (e) => {
    e.preventDefault();
    setBrandLoading(true); setBrandMsg({ type: '', text: '' });
    try {
      await api.put('/admin/profile/branding', { customColor: brandColor || null });
      setBrandMsg({ type: 'success', text: 'Farbe gespeichert' });
    } catch {
      setBrandMsg({ type: 'error', text: 'Fehler beim Speichern' });
    } finally { setBrandLoading(false); }
  };

  const saveCustomUrl = async (e) => {
    e.preventDefault();
    setUrlLoading(true); setUrlMsg({ type: '', text: '' });
    try {
      const r = await api.put('/admin/profile/custom-url', { slug: urlSlug });
      setUrlSlug(r.data.slug);
      setProfile(p => ({ ...p, slug: r.data.slug }));
      setUrlMsg({ type: 'success', text: 'URL gespeichert' });
    } catch (err) {
      setUrlMsg({ type: 'error', text: err.response?.data?.error || 'Fehler beim Speichern' });
    } finally { setUrlLoading(false); }
  };

  const copyReferralCode = () => {
    const code = referrals?.referralCode;
    if (!code) return;
    if (navigator.clipboard) { navigator.clipboard.writeText(code); }
    else {
      const el = document.createElement('textarea');
      el.value = code; el.style.cssText = 'position:fixed;opacity:0'; document.body.appendChild(el);
      el.select(); document.execCommand('copy'); document.body.removeChild(el);
    }
    setUrlMsg({ type: 'success', text: 'Code kopiert!' });
    setTimeout(() => setUrlMsg({ type: '', text: '' }), 2000);
  };

  const startInfoEdit = () => {
    setInfoForm({
      name: profile?.name || '',
      city: profile?.city || '',
      plz: profile?.plz || '',
      address: profile?.address || '',
      website: profile?.website || '',
      contactName: profile?.contact_name || '',
      contactRole: profile?.contact_role || '',
      contactEmail: profile?.contact_email || '',
      contactPhone: profile?.contact_phone || '',
    });
    setInfoEditing(true);
    setInfoMsg({ type: '', text: '' });
  };

  const saveInfo = async (e) => {
    e.preventDefault();
    setInfoLoading(true); setInfoMsg({ type: '', text: '' });
    try {
      await api.put('/admin/profile/info', infoForm);
      setProfile(p => ({ ...p, name: infoForm.name, city: infoForm.city, plz: infoForm.plz, address: infoForm.address, website: infoForm.website, contact_name: infoForm.contactName, contact_role: infoForm.contactRole, contact_email: infoForm.contactEmail, contact_phone: infoForm.contactPhone }));
      setInfoEditing(false);
      setInfoMsg({ type: 'success', text: 'Daten gespeichert' });
    } catch {
      setInfoMsg({ type: 'error', text: 'Fehler beim Speichern' });
    } finally { setInfoLoading(false); }
  };

  const createSubAdmin = async (e) => {
    e.preventDefault();
    setSubCreating(true); setSubMsg({ type: '', text: '' });
    try {
      const r = await api.post('/admin/sub-admins', newSub);
      setSubAdmins(p => [...p, r.data.subAdmin]);
      setNewSub({ name: '', email: '', password: '', role: 'admin' });
      setSubMsg({ type: 'success', text: 'Admin erstellt' });
    } catch (err) {
      setSubMsg({ type: 'error', text: err.response?.data?.error || 'Fehler beim Erstellen' });
    } finally { setSubCreating(false); }
  };

  const deleteSubAdmin = async (id) => {
    if (!window.confirm('Admin-Zugang löschen?')) return;
    try {
      await api.delete(`/admin/sub-admins/${id}`);
      setSubAdmins(p => p.filter(s => s.id !== id));
      toast('Admin-Zugang gelöscht', 'success');
    } catch (err) {
      toast(err.response?.data?.error || 'Fehler', 'error');
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwForm.next !== pwForm.confirm) {
      setPwMsg({ type: 'error', text: 'Passwörter stimmen nicht überein' });
      return;
    }
    if (pwForm.next.length < 8 || !/[A-Z]/.test(pwForm.next) || !/[0-9]/.test(pwForm.next)) {
      setPwMsg({ type: 'error', text: 'Mind. 8 Zeichen, ein Großbuchstabe und eine Zahl' });
      return;
    }
    setPwLoading(true);
    setPwMsg({ type: '', text: '' });
    try {
      await api.post('/auth/mosque/change-password', {
        currentPassword: pwForm.current,
        newPassword: pwForm.next,
      });
      setPwMsg({ type: 'success', text: 'Passwort erfolgreich geändert' });
      setPwForm({ current: '', next: '', confirm: '' });
    } catch (err) {
      const msg = err.response?.data?.error;
      setPwMsg({ type: 'error', text: msg === 'Current password is incorrect' ? 'Aktuelles Passwort ist falsch' : msg || 'Fehler beim Ändern' });
    } finally {
      setPwLoading(false);
    }
  };

  const plan = profile || authPlan;
  const planKey = plan?.plan || 'free';
  const planInfo = PLAN_INFO[planKey] || PLAN_INFO.free;

  const daysLeft = plan?.expires_at
    ? Math.max(0, Math.ceil((new Date(plan.expires_at) - new Date()) / 86400000))
    : null;

  return (
    <div style={{ display: 'flex' }}>
      <AdminSidebar />
      <main className="main-content">
        <div className="page-header">
          <h1>Profil & Einstellungen</h1>
          <p>{authMosque?.name}</p>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: '1px solid var(--border)', paddingBottom: 0 }}>
          {[
            { key: 'info', label: 'Moschee-Info' },
            { key: 'team', label: 'Team / Admins' },
            { key: 'plan', label: 'Mein Plan' },
            { key: 'branding', label: 'Branding' },
            { key: 'categories', label: 'Kategorien' },
            { key: 'password', label: 'Passwort ändern' },
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} style={{
              padding: '10px 18px',
              fontSize: 14,
              fontWeight: tab === t.key ? 600 : 500,
              color: tab === t.key ? 'var(--primary)' : 'var(--text3)',
              background: 'none',
              border: 'none',
              borderBottom: tab === t.key ? '2px solid var(--primary)' : '2px solid transparent',
              marginBottom: -1,
              cursor: 'pointer',
              transition: 'all 0.15s',
            }}>
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="spinner-wrap"><div className="spinner" /></div>
        ) : (
          <>
            {/* Tab: Info */}
            {tab === 'info' && profile && (
              <div style={{ maxWidth: 680 }}>
                <div className="card">
                  <h3 style={{ fontSize: 14, color: 'var(--text3)', marginBottom: 20, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Moschee-Informationen
                  </h3>

                  {/* Logo upload */}
                  <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 20 }}>
                    <div style={{ flexShrink: 0 }}>
                      {profile.logo_path ? (
                        <img src={profile.logo_path} alt="Logo" style={{ width: 80, height: 80, borderRadius: 12, objectFit: 'cover', border: '2px solid var(--border)' }} />
                      ) : (
                        <div style={{ width: 80, height: 80, borderRadius: 12, background: 'var(--bg)', border: '2px dashed var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text3)', fontSize: 28 }}>
                          M
                        </div>
                      )}
                    </div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                        Moschee-Logo
                        {!hasFeature('custom_branding') && (
                          <span style={{ marginLeft: 8, fontSize: 10, background: '#F3F4F6', color: '#6b7280', padding: '2px 7px', borderRadius: 100, fontWeight: 700 }}>
                            Standard+
                          </span>
                        )}
                      </div>
                      {hasFeature('custom_branding') ? (
                        <>
                          <input ref={logoInputRef} type="file" accept="image/*" style={{ display: 'none' }}
                            onChange={e => uploadLogo(e.target.files[0])} />
                          <button type="button" className="btn btn-sm btn-secondary"
                            disabled={logoUploading} onClick={() => logoInputRef.current?.click()}>
                            {logoUploading ? 'Uploading...' : profile.logo_path ? 'Logo andern' : 'Logo hochladen'}
                          </button>
                          {logoMsg && <div style={{ fontSize: 12, marginTop: 6, color: logoMsg.includes('erfolgreich') ? 'var(--primary)' : 'var(--error)' }}>{logoMsg}</div>}
                          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>JPG, PNG, max. 4 MB</div>
                        </>
                      ) : (
                        <div style={{ fontSize: 13, color: '#9ca3af' }}>
                          Logo-Upload ab dem Standard-Plan.{' '}
                          <a href="https://ilmify.ch/#pricing" target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', fontWeight: 600 }}>Upgraden</a>
                        </div>
                      )}
                    </div>
                  </div>

                  {infoMsg.text && (
                    <div className={`alert alert-${infoMsg.type === 'error' ? 'error' : 'success'}`} style={{ marginBottom: 16 }}>
                      {infoMsg.text}
                    </div>
                  )}

                  {infoEditing ? (
                    <form onSubmit={saveInfo}>
                      <div className="grid-2" style={{ gap: 16 }}>
                        <div className="form-group">
                          <label className="form-label">Name der Moschee</label>
                          <input className="form-input" value={infoForm.name} onChange={e => setInfoForm(p => ({ ...p, name: e.target.value }))} required />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Website</label>
                          <input className="form-input" value={infoForm.website} onChange={e => setInfoForm(p => ({ ...p, website: e.target.value }))} />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Strasse & Hausnummer</label>
                          <input className="form-input" value={infoForm.address} onChange={e => setInfoForm(p => ({ ...p, address: e.target.value }))} placeholder="Musterstrasse 12" />
                        </div>
                        <div className="form-group">
                          <label className="form-label">PLZ</label>
                          <input className="form-input" value={infoForm.plz} onChange={e => setInfoForm(p => ({ ...p, plz: e.target.value }))} placeholder="8001" maxLength={10} />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Stadt</label>
                          <input className="form-input" value={infoForm.city} onChange={e => setInfoForm(p => ({ ...p, city: e.target.value }))} placeholder="Zürich" required />
                        </div>
                      </div>
                      <div style={{ borderTop: '1px solid var(--border)', margin: '16px 0', paddingTop: 16 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text3)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>Kontaktperson</div>
                        <div className="grid-2" style={{ gap: 16 }}>
                          <div className="form-group">
                            <label className="form-label">Name</label>
                            <input className="form-input" value={infoForm.contactName} onChange={e => setInfoForm(p => ({ ...p, contactName: e.target.value }))} />
                          </div>
                          <div className="form-group">
                            <label className="form-label">Funktion</label>
                            <input className="form-input" value={infoForm.contactRole} onChange={e => setInfoForm(p => ({ ...p, contactRole: e.target.value }))} />
                          </div>
                          <div className="form-group">
                            <label className="form-label">E-Mail</label>
                            <input className="form-input" type="email" value={infoForm.contactEmail} onChange={e => setInfoForm(p => ({ ...p, contactEmail: e.target.value }))} />
                          </div>
                          <div className="form-group">
                            <label className="form-label">Telefon</label>
                            <input className="form-input" value={infoForm.contactPhone} onChange={e => setInfoForm(p => ({ ...p, contactPhone: e.target.value }))} />
                          </div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 10 }}>
                        <button className="btn btn-primary" type="submit" disabled={infoLoading}>
                          {infoLoading ? 'Speichern...' : 'Speichern'}
                        </button>
                        <button className="btn btn-ghost" type="button" onClick={() => setInfoEditing(false)}>Abbrechen</button>
                      </div>
                    </form>
                  ) : (
                    <>
                      <div className="grid-2" style={{ gap: 20 }}>
                        <InfoRow label="Name" value={profile.name} />
                        <InfoRow label="Land" value={profile.country_name || profile.country_code} />
                        <InfoRow label="Strasse & Nr." value={profile.address || '—'} />
                        <InfoRow label="PLZ" value={profile.plz || '—'} />
                        <InfoRow label="Stadt" value={profile.city} />
                        <InfoRow label="Website" value={profile.website || '—'} />
                        <InfoRow label="Anmelde-E-Mail" value={profile.login_email} mono />
                      </div>

                      <div style={{ margin: '20px 0', borderTop: '1px solid var(--border)', paddingTop: 20 }}>
                        <h3 style={{ fontSize: 14, color: 'var(--text3)', marginBottom: 16, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          Kontaktperson
                        </h3>
                        <div className="grid-2" style={{ gap: 20 }}>
                          <InfoRow label="Name" value={profile.contact_name} />
                          <InfoRow label="Funktion" value={profile.contact_role} />
                          <InfoRow label="E-Mail" value={profile.contact_email} />
                          <InfoRow label="Telefon" value={profile.contact_phone || '—'} />
                        </div>
                      </div>

                      <button className="btn btn-secondary btn-sm" onClick={startInfoEdit} style={{ marginTop: 4 }}>
                        Daten bearbeiten
                      </button>
                    </>
                  )}

                  <div style={{ marginTop: 16, padding: '10px 14px', background: 'var(--bg)', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 12, color: 'var(--text3)' }}>Moschee-Code:</span>
                    <span style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--primary)', letterSpacing: 2 }}>
                      {profile.auto_code}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Tab: Team / Admins */}
            {tab === 'team' && (
              <div style={{ maxWidth: 680 }}>
                <div className="card" style={{ marginBottom: 16 }}>
                  <h3 style={{ fontSize: 14, marginBottom: 4 }}>Team-Zugänge</h3>
                  <p style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 20 }}>
                    Mitarbeiter erhalten einen eigenen Login und haben vollen Zugang zum Admin-Bereich dieser Moschee.
                  </p>

                  {subAdminsLoading ? (
                    <div className="spinner-wrap" style={{ minHeight: 60 }}><div className="spinner" /></div>
                  ) : (
                    <>
                      {subAdmins.length > 0 && (
                        <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', marginBottom: 20 }}>
                          {subAdmins.map((s, i) => (
                            <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: i < subAdmins.length - 1 ? '1px solid var(--border)' : 'none' }}>
                              <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--primary-lt)', color: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 15, flexShrink: 0 }}>
                                {s.name?.[0]?.toUpperCase() || '?'}
                              </div>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 600, fontSize: 14 }}>{s.name}</div>
                                <div style={{ fontSize: 12, color: 'var(--text3)' }}>{s.email}</div>
                              </div>
                              <span style={{ fontSize: 11, background: 'var(--bg)', color: 'var(--text3)', padding: '3px 8px', borderRadius: 100, border: '1px solid var(--border)' }}>
                                {s.role}
                              </span>
                              <button className="btn btn-sm btn-danger" onClick={() => deleteSubAdmin(s.id)}>Entfernen</button>
                            </div>
                          ))}
                        </div>
                      )}

                      <h4 style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Neuen Admin hinzufügen</h4>
                      {subMsg.text && (
                        <div className={`alert alert-${subMsg.type === 'error' ? 'error' : 'success'}`} style={{ marginBottom: 12 }}>
                          {subMsg.text}
                        </div>
                      )}
                      <form onSubmit={createSubAdmin}>
                        <div className="grid-2" style={{ gap: 12 }}>
                          <div className="form-group">
                            <label className="form-label">Name *</label>
                            <input className="form-input" value={newSub.name} onChange={e => setNewSub(p => ({ ...p, name: e.target.value }))} required placeholder="Max Mustermann" />
                          </div>
                          <div className="form-group">
                            <label className="form-label">E-Mail *</label>
                            <input className="form-input" type="email" value={newSub.email} onChange={e => setNewSub(p => ({ ...p, email: e.target.value }))} required placeholder="admin@moschee.ch" />
                          </div>
                          <div className="form-group">
                            <label className="form-label">Passwort *</label>
                            <input className="form-input" type="password" value={newSub.password} onChange={e => setNewSub(p => ({ ...p, password: e.target.value }))} required minLength={8} placeholder="Mind. 8 Zeichen" />
                          </div>
                          <div className="form-group">
                            <label className="form-label">Rolle</label>
                            <select className="form-select" value={newSub.role} onChange={e => setNewSub(p => ({ ...p, role: e.target.value }))}>
                              <option value="admin">Admin (voller Zugang)</option>
                              <option value="viewer">Betrachter (nur lesen)</option>
                            </select>
                          </div>
                        </div>
                        <button className="btn btn-primary" type="submit" disabled={subCreating} style={{ marginTop: 4 }}>
                          {subCreating ? 'Erstelle...' : '+ Admin hinzufügen'}
                        </button>
                      </form>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Tab: Plan */}
            {tab === 'plan' && (
              <div style={{ maxWidth: 680 }}>
                {/* Current plan */}
                <div className="card" style={{ marginBottom: 20 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
                    <div>
                      <div style={{ fontSize: 12, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Aktueller Plan</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: 24, fontWeight: 700, fontFamily: 'Playfair Display', color: planInfo.color }}>
                          {planInfo.label}
                        </span>
                        {plan?.is_trial && (
                          <span className="badge badge-orange">Testphase</span>
                        )}
                      </div>
                      <p style={{ fontSize: 13, color: 'var(--text2)', marginTop: 6 }}>{planInfo.features}</p>
                    </div>
                  </div>

                  {daysLeft !== null && (
                    <div style={{
                      padding: '10px 14px', borderRadius: 8,
                      background: daysLeft <= 7 ? 'var(--error-lt)' : 'var(--primary-lt)',
                      color: daysLeft <= 7 ? 'var(--error)' : 'var(--primary)',
                    }}>
                      {daysLeft === 0
                        ? 'Plan abgelaufen — bitte kontaktieren Sie uns'
                        : `Gültig bis: ${new Date(plan.expires_at).toLocaleDateString('de-CH')} · noch ${daysLeft} Tage`}
                    </div>
                  )}
                </div>

                {/* Plan comparison */}
                {planKey !== 'premium' ? (
                  <div className="card">
                    <h3 style={{ fontSize: 14, marginBottom: 16 }}>Plan upgraden</h3>
                    <div className="grid-3" style={{ gap: 16, marginBottom: 16 }}>
                      {Object.entries(PLAN_INFO).map(([key, info]) => {
                        const priceChf = key === 'standard'
                          ? planPrices.standard_price_chf
                          : key === 'premium'
                          ? planPrices.premium_price_chf
                          : null;
                        return (
                          <div key={key} style={{
                            border: key === planKey ? '2px solid var(--primary)' : '1px solid var(--border)',
                            borderRadius: 10, padding: 16,
                            background: key === planKey ? 'var(--primary-lt)' : 'var(--surface)',
                          }}>
                            <div style={{ fontWeight: 700, color: info.color, marginBottom: 2 }}>{info.label}</div>
                            {key === 'free' ? (
                              <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>
                                0 CHF
                              </div>
                            ) : priceChf !== null ? (
                              <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>
                                {priceChf % 1 === 0 ? priceChf : priceChf.toFixed(2)} CHF
                                <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--text3)' }}> / Monat</span>
                              </div>
                            ) : null}
                            <div style={{ fontSize: 12, color: 'var(--text2)' }}>{info.features}</div>
                            {key === planKey && (
                              <div style={{ marginTop: 8, fontSize: 12, fontWeight: 600, color: 'var(--primary)', display: 'flex', alignItems: 'center', gap: 4 }}>
                                <Check size={12} /> Ihr aktueller Plan
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <p style={{ fontSize: 13, color: 'var(--text3)' }}>
                      Für einen Upgrade kontaktieren Sie uns: <strong>support@ilmify.ch</strong>
                    </p>
                  </div>
                ) : (
                  <div className="card" style={{ background: 'linear-gradient(135deg, #fef9e7 0%, #fefce8 100%)', border: '1px solid #fcd34d' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                      <div style={{ fontSize: 32 }}>
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#8B6A1C" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                      </div>
                      <div>
                        <div style={{ fontWeight: 700, color: '#8B6A1C', fontSize: 15 }}>Sie haben den höchsten Plan</div>
                        <div style={{ fontSize: 13, color: '#92400E', marginTop: 2 }}>
                          Premium — alle Funktionen freigeschaltet · Unbegrenzte Nutzung
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Referral Code */}
                {hasFeature('referral') && (
                  <div className="card" style={{ marginTop: 16 }}>
                    <h3 style={{ fontSize: 14, marginBottom: 16 }}>Empfehlungscode</h3>
                    {referrals ? (
                      <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, padding: '12px 16px', background: 'var(--bg)', borderRadius: 8 }}>
                          <span style={{ fontFamily: 'monospace', fontSize: 22, fontWeight: 700, color: 'var(--primary)', letterSpacing: 4 }}>
                            {referrals.referralCode}
                          </span>
                          <button className="btn btn-sm btn-secondary" onClick={copyReferralCode}>Kopieren</button>
                        </div>
                        {urlMsg.text && <div className="alert alert-success" style={{ marginBottom: 12 }}>{urlMsg.text}</div>}
                        <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 12 }}>
                          Teilen Sie diesen Code mit anderen Moscheen. Wenn sie sich mit Ihrem Code registrieren, werden sie als Ihre Empfehlung gezählt.
                        </div>
                        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>
                          {referrals.referrals.length} Moschee{referrals.referrals.length !== 1 ? 'n' : ''} empfohlen
                        </div>
                        {referrals.referrals.length > 0 && (
                          <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                            {referrals.referrals.map((r, i) => (
                              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderBottom: i < referrals.referrals.length - 1 ? '1px solid var(--border)' : 'none' }}>
                                <div>
                                  <div style={{ fontWeight: 600, fontSize: 13 }}>{r.name}</div>
                                  <div style={{ fontSize: 11, color: 'var(--text3)' }}>{r.city} · {new Date(r.created_at).toLocaleDateString('de-CH')}</div>
                                </div>
                                <span style={{ fontSize: 11, background: '#ECFDF5', color: '#065F46', padding: '3px 8px', borderRadius: 100, fontWeight: 600 }}>
                                  {r.plan || 'free'}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="spinner-wrap" style={{ minHeight: 60 }}><div className="spinner" /></div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Tab: Branding */}
            {tab === 'branding' && (
              <div style={{ maxWidth: 480 }}>
                {!hasFeature('custom_branding') && (
                  <div style={{
                    background: '#FEF9E7', border: '1px solid #F59E0B', borderRadius: 10,
                    padding: '14px 18px', marginBottom: 20, display: 'flex', alignItems: 'flex-start', gap: 12,
                  }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 13, color: '#92400E', marginBottom: 4 }}>
                        Branding nicht verfugbar
                      </div>
                      <div style={{ fontSize: 12, color: '#92400E' }}>
                        Custom Branding ist ab dem Standard-Plan verfugbar.{' '}
                        <a href="https://ilmify.ch/#pricing" target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', fontWeight: 600 }}>
                          Plan upgraden
                        </a>
                      </div>
                    </div>
                  </div>
                )}
                <div className="card">
                  <h3 style={{ fontSize: 16, marginBottom: 8 }}>Quiz-Branding</h3>
                  <p style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 20 }}>
                    Die Akzentfarbe wird in der Quiz-Oberflache als Highlight-Farbe verwendet.
                  </p>

                  {brandMsg.text && (
                    <div className={`alert alert-${brandMsg.type === 'error' ? 'error' : 'success'}`} style={{ marginBottom: 16 }}>
                      {brandMsg.text}
                    </div>
                  )}

                  <form onSubmit={saveBranding}>
                    <div className="form-group">
                      <label className="form-label">Akzentfarbe (Hex)</label>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                        <input
                          type="color"
                          value={brandColor || '#5B8A75'}
                          onChange={e => setBrandColor(e.target.value)}
                          style={{ width: 48, height: 40, border: '1px solid var(--border)', borderRadius: 8, cursor: 'pointer', padding: 2, background: 'var(--surface)' }}
                        />
                        <input
                          className="form-input"
                          type="text"
                          value={brandColor}
                          onChange={e => setBrandColor(e.target.value)}
                          placeholder="#5B8A75"
                          pattern="^#[0-9A-Fa-f]{6}$"
                          style={{ flex: 1, fontFamily: 'monospace' }}
                        />
                        {brandColor && (
                          <button
                            type="button"
                            onClick={() => setBrandColor('')}
                            style={{ fontSize: 12, color: 'var(--text3)', background: 'none', border: 'none', cursor: 'pointer' }}
                          >
                            Reset
                          </button>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>
                        Standard: Moschee-Grün (#5B8A75). Leer lassen für Standard.
                      </div>
                    </div>

                    {brandColor && (
                      <div style={{ marginBottom: 20, padding: '12px 16px', borderRadius: 10, background: `${brandColor}18`, border: `2px solid ${brandColor}` }}>
                        <span style={{ fontWeight: 700, color: brandColor }}>Vorschau: Diese Farbe wird als Akzent verwendet</span>
                      </div>
                    )}

                    <button className="btn btn-primary" type="submit" disabled={brandLoading || !hasFeature('custom_branding')}>
                      {brandLoading ? 'Speichern...' : 'Farbe speichern'}
                    </button>
                  </form>
                </div>

                {/* Custom URL */}
                <div className="card" style={{ marginTop: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                    <h3 style={{ fontSize: 16, margin: 0 }}>Eigene URL</h3>
                    {!hasFeature('custom_url') && (
                      <span style={{ fontSize: 10, background: '#F3F4F6', color: '#6b7280', padding: '2px 7px', borderRadius: 100, fontWeight: 700 }}>Premium</span>
                    )}
                  </div>
                  {hasFeature('custom_url') ? (
                    <form onSubmit={saveCustomUrl}>
                      <div className="form-group">
                        <label className="form-label">URL-Slug</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 0, border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                          <span style={{ padding: '10px 12px', background: 'var(--bg)', color: 'var(--text3)', fontSize: 13, whiteSpace: 'nowrap', borderRight: '1px solid var(--border)' }}>
                            ilmify.ch/mosque/
                          </span>
                          <input
                            style={{ flex: 1, border: 'none', outline: 'none', padding: '10px 12px', fontSize: 14, background: 'var(--surface)' }}
                            value={urlSlug}
                            onChange={e => setUrlSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                            placeholder="ihre-moschee"
                            minLength={3} maxLength={60} required
                          />
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>Nur Kleinbuchstaben, Zahlen und Bindestriche</div>
                      </div>
                      {urlMsg.text && <div className={`alert alert-${urlMsg.type === 'error' ? 'error' : 'success'}`} style={{ marginBottom: 12 }}>{urlMsg.text}</div>}
                      <button className="btn btn-primary" type="submit" disabled={urlLoading}>
                        {urlLoading ? 'Speichern...' : 'URL speichern'}
                      </button>
                    </form>
                  ) : (
                    <p style={{ color: 'var(--text3)', fontSize: 13 }}>
                      Eigene URL ab dem Premium-Plan.{' '}
                      <a href="https://ilmify.ch/#pricing" target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', fontWeight: 600 }}>Upgraden</a>
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Tab: Categories */}
            {tab === 'categories' && (
              <div style={{ maxWidth: 680 }}>
                <MosqueCategoriesManager mosqueId={authMosque?.id} />
              </div>
            )}

            {/* Tab: Password */}
            {tab === 'password' && (
              <div style={{ maxWidth: 480 }}>
                <div className="card">
                  <h3 style={{ fontSize: 16, marginBottom: 20 }}>Passwort ändern</h3>

                  {pwMsg.text && (
                    <div className={`alert alert-${pwMsg.type === 'error' ? 'error' : 'success'}`}>
                      {pwMsg.text}
                    </div>
                  )}

                  <form onSubmit={changePassword}>
                    <div className="form-group">
                      <label className="form-label">Aktuelles Passwort *</label>
                      <input
                        className="form-input"
                        type="password"
                        value={pwForm.current}
                        onChange={e => setPwForm(p => ({ ...p, current: e.target.value }))}
                        required
                        autoComplete="current-password"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Neues Passwort *</label>
                      <input
                        className="form-input"
                        type="password"
                        value={pwForm.next}
                        onChange={e => setPwForm(p => ({ ...p, next: e.target.value }))}
                        required
                        minLength={8}
                        autoComplete="new-password"
                      />
                      <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>
                        Mind. 8 Zeichen · ein Großbuchstabe · eine Zahl
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Passwort bestätigen *</label>
                      <input
                        className="form-input"
                        type="password"
                        value={pwForm.confirm}
                        onChange={e => setPwForm(p => ({ ...p, confirm: e.target.value }))}
                        required
                        autoComplete="new-password"
                      />
                    </div>

                    <button className="btn btn-primary" type="submit" disabled={pwLoading}>
                      {pwLoading ? 'Speichern...' : 'Passwort ändern'}
                    </button>
                  </form>
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}

function MosqueCategoriesManager({ mosqueId }) {
  const toast = useToast();
  const [cats, setCats] = useState([]);
  const [ownCats, setOwnCats] = useState([]);
  const [form, setForm] = useState({ nameDe: '', nameAl: '', icon: '' });
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const load = async () => {
    const r = await api.get('/admin/categories');
    const all = r.data.categories || [];
    setCats(all);
    setOwnCats(all.filter(c => c.mosque_id));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.nameDe || !form.nameAl) return setMsg('Name DE und AL pflichtfeld');
    setSaving(true);
    try {
      if (editId) {
        await api.put(`/admin/categories/${editId}`, form);
      } else {
        await api.post('/admin/categories', form);
      }
      setForm({ nameDe: '', nameAl: '', icon: '' });
      setEditId(null);
      await load();
      setMsg('');
    } catch (err) {
      setMsg(err.response?.data?.error || 'Fehler');
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Kategorie löschen?')) return;
    try {
      await api.delete(`/admin/categories/${id}`);
      await load();
      toast('Kategorie gelöscht', 'success');
    } catch (err) {
      toast(err.response?.data?.error || 'Fehler', 'error');
    }
  };

  return (
    <div className="card">
      <h3 style={{ fontSize: 14, color: 'var(--text3)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>Eigene Kategorien</h3>
      <p style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 20 }}>Globale Kategorien ({cats.filter(c => !c.mosque_id).length}) stehen automatisch zur Verfügung. Hier eigene hinzufügen.</p>

      {ownCats.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
          {ownCats.map(c => (
            <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px', background: 'var(--surface2)', borderRadius: 8 }}>
              <div style={{ flex: 1 }}>
                <span style={{ fontWeight: 600, fontSize: 14 }}>{c.name_de}</span>
                <span style={{ color: 'var(--text3)', fontSize: 13, marginLeft: 10 }}>{c.name_al}</span>
              </div>
              <button className="btn btn-sm btn-ghost" onClick={() => { setEditId(c.id); setForm({ nameDe: c.name_de, nameAl: c.name_al, icon: c.icon || '' }); }}>Bearbeiten</button>
              <button className="btn btn-sm btn-danger" onClick={() => del(c.id)}>Löschen</button>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8, alignItems: 'end' }}>
        <div>
          <label className="form-label" style={{ fontSize: 11 }}>Name Deutsch *</label>
          <input className="form-input" value={form.nameDe} onChange={e => setForm(p => ({ ...p, nameDe: e.target.value }))} placeholder="z.B. Gjuha Shqipe" />
        </div>
        <div>
          <label className="form-label" style={{ fontSize: 11 }}>Name Shqip *</label>
          <input className="form-input" value={form.nameAl} onChange={e => setForm(p => ({ ...p, nameAl: e.target.value }))} placeholder="p.sh. Gjuha Shqipe" />
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{editId ? 'Speichern' : '+ Hinzufügen'}</button>
          {editId && <button className="btn btn-ghost" onClick={() => { setEditId(null); setForm({ nameDe: '', nameAl: '', icon: '' }); }}>Abbrechen</button>}
        </div>
      </div>
      {msg && <p style={{ marginTop: 8, fontSize: 12, color: 'var(--error)' }}>{msg}</p>}
    </div>
  );
}

const InfoRow = ({ label, value, mono }) => (
  <div>
    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
      {label}
    </div>
    <div style={{
      fontSize: 14, color: 'var(--text)',
      fontFamily: mono ? 'monospace' : 'inherit',
      fontWeight: mono ? 600 : 400,
    }}>
      {value || '—'}
    </div>
  </div>
);
